import { useEffect, useId, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { useReducedMotion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useParams } from 'react-router-dom';
import Layout from '../layout/Layout';
import AcceptanceHero from '../components/scholarshipAcceptance/AcceptanceHero';
import AddressForm from '../components/scholarshipAcceptance/AddressForm';
import {
  ORDERED_ACCEPTANCE_FIELDS,
  buildAcceptancePayload,
  extractErrorMessage,
  formatAddressSummary,
  hydrateAcceptanceForm,
  validateAcceptanceField,
  validateAcceptanceForm,
} from '../components/scholarshipAcceptance/acceptanceValidation';
import {
  OTHER_CITY_OPTION,
  type AcceptanceFieldErrors,
  type AcceptanceFieldKey,
  type AcceptanceFormValues,
  type ScholarshipAcceptanceResponse,
} from '../components/scholarshipAcceptance/types';
import scholarshipAcceptanceService from '../services/scholarshipAcceptance.service';

const INITIAL_VALUES: AcceptanceFormValues = {
  social_share_consent: null,
  recipient_name: '',
  governorate: '',
  cityChoice: '',
  customCity: '',
  area: '',
  street: '',
  building: '',
  floor: '',
  apartment: '',
  landmark: '',
  postal_code: '',
  phone: '',
  backup_phone: '',
  backup_phone_relation: '',
};

function cleanServerMessage(detail: unknown, fallback: string): string {
  return extractErrorMessage(detail, fallback).replace(/^Value error,\s*/i, '');
}

export default function ScholarshipAcceptance() {
  const { token = '' } = useParams();
  const formId = useId();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [profile, setProfile] = useState<ScholarshipAcceptanceResponse | null>(null);
  const [values, setValues] = useState<AcceptanceFormValues>(INITIAL_VALUES);
  const [touched, setTouched] = useState<Partial<Record<AcceptanceFieldKey, boolean>>>({});
  const [errors, setErrors] = useState<AcceptanceFieldErrors>({});

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedInSession, setSavedInSession] = useState(false);

  const savingRef = useRef(false);
  const successCardRef = useRef<HTMLElement | null>(null);
  const successHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const formTopRef = useRef<HTMLDivElement | null>(null);
  const shouldScrollFormRef = useRef(false);

  const prefersReducedMotion = Boolean(useReducedMotion());
  const scrollBehavior: ScrollBehavior = prefersReducedMotion ? 'instant' : 'smooth';

  useEffect(() => {
    document.title = 'خطاب القبول — منحة EduNasser';
  }, []);

  useEffect(() => {
    if (savedInSession) {
      if (typeof successCardRef.current?.scrollIntoView === 'function') {
        successCardRef.current.scrollIntoView({
          behavior: scrollBehavior,
          block: 'start',
        });
      }
      successHeadingRef.current?.focus({ preventScroll: true });
    } else if (shouldScrollFormRef.current) {
      shouldScrollFormRef.current = false;
      if (typeof formTopRef.current?.scrollIntoView === 'function') {
        formTopRef.current.scrollIntoView({
          behavior: scrollBehavior,
          block: 'start',
        });
      }
    }
  }, [savedInSession, scrollBehavior]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setLoadError(false);

    scholarshipAcceptanceService
      .get(token)
      .then((data) => {
        if (cancelled) return;
        setProfile(data);
        setValues(hydrateAcceptanceForm(data));
        setTouched({});
        setErrors({});
        setSavedInSession(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (isAxiosError(error) && error.response?.status === 404) {
          setNotFound(true);
        } else {
          setLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, reloadKey]);

  const mapKeyToField = (key: keyof AcceptanceFormValues): AcceptanceFieldKey => {
    if (key === 'cityChoice' || key === 'customCity') return 'city';
    return key;
  };

  const handleFieldChange = <K extends keyof AcceptanceFormValues>(
    key: K,
    value: AcceptanceFormValues[K]
  ) => {
    const nextValues: AcceptanceFormValues = { ...values, [key]: value };
    setValues(nextValues);
    setSaveError(null);

    const field = mapKeyToField(key);
    if (touched[field] || errors[field]) {
      const nextErr = validateAcceptanceField(field, nextValues);
      setErrors((prev) => ({ ...prev, [field]: nextErr }));
    }
    if (key === 'phone' && (touched.backup_phone || errors.backup_phone)) {
      const backupErr = validateAcceptanceField('backup_phone', nextValues);
      setErrors((prev) => ({ ...prev, backup_phone: backupErr }));
    }
  };

  const handleGovernorateChange = (governorate: string) => {
    const nextValues: AcceptanceFormValues = {
      ...values,
      governorate,
      cityChoice: '',
      customCity: '',
    };
    setValues(nextValues);
    setSaveError(null);
    setErrors((prev) => ({
      ...prev,
      governorate:
        touched.governorate || prev.governorate
          ? validateAcceptanceField('governorate', nextValues)
          : undefined,
      city: undefined,
    }));
  };

  const handleFloorChange = (floor: string) => {
    const nextValues: AcceptanceFormValues = {
      ...values,
      floor,
      apartment: floor === 'house' ? '' : values.apartment,
    };
    setValues(nextValues);
    setSaveError(null);
    setErrors((prev) => ({
      ...prev,
      floor:
        touched.floor || prev.floor
          ? validateAcceptanceField('floor', nextValues)
          : undefined,
      apartment:
        floor === 'house'
          ? undefined
          : touched.apartment || prev.apartment
            ? validateAcceptanceField('apartment', nextValues)
            : undefined,
    }));
  };

  const handleFieldBlur = (field: AcceptanceFieldKey) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const err = validateAcceptanceField(field, values);
    setErrors((prev) => ({ ...prev, [field]: err }));
  };

  const focusFirstInvalidField = (
    nextErrors: AcceptanceFieldErrors,
    currentValues: AcceptanceFormValues
  ) => {
    const firstField = ORDERED_ACCEPTANCE_FIELDS.find((f) => Boolean(nextErrors[f]));
    if (!firstField) return;

    const targetId =
      firstField === 'city' && currentValues.cityChoice === OTHER_CITY_OPTION
        ? `${formId}-city-custom`
        : `${formId}-${firstField}`;

    const el = document.getElementById(targetId);
    if (!el) return;

    // The share question's radios are visually hidden, so scroll its whole card instead;
    // the card's scroll margin keeps it clear of the floating site header.
    const scrollTarget =
      firstField === 'social_share_consent'
        ? document.getElementById(`${formId}-social_share_section`) ?? el
        : el;
    if (typeof scrollTarget.scrollIntoView === 'function') {
      scrollTarget.scrollIntoView({
        behavior: scrollBehavior,
        block: firstField === 'social_share_consent' ? 'start' : 'center',
      });
    }
    el.focus({ preventScroll: true });
  };

  const handleSubmit = async () => {
    if (savingRef.current) return;

    const nextErrors = validateAcceptanceForm(values);
    setErrors(nextErrors);
    setSaveError(null);

    if (Object.keys(nextErrors).length > 0) {
      const allTouched: Partial<Record<AcceptanceFieldKey, boolean>> = {};
      for (const key of ORDERED_ACCEPTANCE_FIELDS) {
        allTouched[key] = true;
      }
      setTouched(allTouched);
      focusFirstInvalidField(nextErrors, values);
      return;
    }

    savingRef.current = true;
    setSaving(true);

    try {
      const payload = buildAcceptancePayload(values);
      const savedData = await scholarshipAcceptanceService.save(token, payload);
      setProfile(savedData);
      setValues(hydrateAcceptanceForm(savedData));
      setSavedInSession(true);
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (error.response?.status === 404) {
          setNotFound(true);
        } else if (!error.response) {
          setSaveError('تأكد من اتصالك بالإنترنت وحاول تاني.');
        } else {
          setSaveError(
            cleanServerMessage(
              error.response.data?.detail,
              'مقدرناش نحفظ البيانات. راجع الخانات وجرّب تاني.'
            )
          );
        }
      } else {
        setSaveError('تأكد من اتصالك بالإنترنت وحاول تاني.');
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div
        dir="rtl"
        lang="ar"
        className="min-h-screen overflow-x-hidden bg-[#FAF9FD] px-4 pb-16 pt-28 text-[#2C2C2C] sm:px-6"
      >
        <div className="mx-auto max-w-xl">
          {loading ? (
            <div
              className="rounded-3xl border border-[#E6E1F2] bg-white p-8 text-center shadow-xs"
              role="status"
            >
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#5B3FD0]" />
              <p className="mt-3 text-sm font-semibold text-[#707070]">
                بنحمّل بياناتك...
              </p>
            </div>
          ) : notFound ? (
            <div
              className="rounded-3xl border border-[#E6E1F2] bg-white p-8 text-center shadow-xs"
              role="alert"
            >
              <h1 className="text-xl font-extrabold text-[#2C2C2C]">
                الرابط غير صحيح
              </h1>
              <p className="mt-3 text-sm leading-7 text-[#707070]">
                الرابط ده مش صحيح. اتأكد إنك فاتح الرابط اللي وصلك في الإيميل.
              </p>
            </div>
          ) : loadError || !profile ? (
            <div
              className="rounded-3xl border border-[#E6E1F2] bg-white p-8 text-center shadow-xs"
              role="alert"
            >
              <h1 className="text-xl font-extrabold text-[#2C2C2C]">
                مقدرناش نحمّل البيانات
              </h1>
              <p className="mt-3 text-sm leading-7 text-[#707070]">
                تأكد من اتصالك بالإنترنت وحاول تاني.
              </p>
              <button
                type="button"
                onClick={() => setReloadKey((k) => k + 1)}
                className="mt-5 min-h-11 rounded-xl bg-[#5B3FD0] px-6 py-3 text-sm font-bold text-white hover:bg-[#4729b0]"
              >
                حاول تاني
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              <AcceptanceHero
                fullName={profile.full_name}
                referenceNumber={profile.reference_number}
                updatedAt={profile.updated_at ?? profile.submitted_at}
                prefersReducedMotion={prefersReducedMotion}
              />

              {savedInSession ? (
                <section
                  ref={successCardRef}
                  role="status"
                  className="scroll-mt-24 rounded-3xl border border-emerald-200 bg-white p-6 text-center shadow-xs sm:p-8"
                >
                  <h2
                    ref={successHeadingRef}
                    tabIndex={-1}
                    className="text-xl font-extrabold text-emerald-800 outline-none sm:text-2xl"
                  >
                    ✅ تمام! بياناتك اتحفظت
                  </h2>
                  <p className="mt-3 rounded-2xl bg-[#FAF9FD] px-4 py-3 text-sm font-semibold leading-7 text-[#2C2C2C]">
                    {formatAddressSummary(values)}
                  </p>
                  {values.social_share_consent === true && (
                    <p className="mt-3 text-sm font-semibold leading-7 text-[#5B3FD0]">
                      شكرًا! مستنيين صورتك وهنشاركها على صفحتنا.
                    </p>
                  )}
                  <p className="mt-3 text-sm leading-7 text-[#707070]">
                    تقدر ترجع لنفس الرابط في أي وقت وتعدّل بياناتك.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      shouldScrollFormRef.current = true;
                      setSavedInSession(false);
                    }}
                    className="mt-5 min-h-11 w-full rounded-xl border-2 border-[#5B3FD0]/25 bg-[#5B3FD0]/5 px-6 py-3 text-sm font-bold text-[#5B3FD0] transition-colors hover:bg-[#5B3FD0]/10 sm:w-auto"
                  >
                    تعديل البيانات
                  </button>
                </section>
              ) : (
                <div ref={formTopRef} className="scroll-mt-24">
                  <AddressForm
                    formId={formId}
                    values={values}
                    errors={errors}
                    saving={saving}
                    saveError={saveError}
                    onFieldChange={handleFieldChange}
                    onGovernorateChange={handleGovernorateChange}
                    onFloorChange={handleFloorChange}
                    onFieldBlur={handleFieldBlur}
                    onSubmit={() => {
                      void handleSubmit();
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
